/**
 * S2 — pure helpers for the Participant Service Segment editor (PRD §14, §16–18, §49, §56).
 * No API access here so the rules can be unit-tested with Jest.
 */
export const RATIOS = ['1:1', '1:2', '1:3', '2:1'];

const minutes = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return -1;
  const [h, m] = hhmm.slice(0, 5).split(':');
  const v = parseInt(h, 10) * 60 + parseInt(m, 10);
  return Number.isFinite(v) ? v : -1;
};

/** Shift duration in hours, overnight-aware (end <= start, crosses_midnight or an end_date mean "next day"). */
export const shiftHours = (shift) => {
  const s = minutes(shift?.start_time);
  let e = minutes(shift?.end_time);
  if (s < 0 || e < 0) return 0;
  const overnight = shift?.crosses_midnight || (shift?.end_date && shift.end_date !== shift.date) || e <= s;
  if (overnight) e += 24 * 60;
  return (e - s) / 60.0;
};

export const emptyForm = (shift) => ({
  participant_service_item_id: '',
  allocation_start_time: shift?.start_time || '',
  allocation_end_time: shift?.end_time || '',
  operational_ratio: shift?.support_ratio || '1:1',
  is_primary: false,
  notes: '',
});

/** Selector options: one per PSI of the ACTIVE agreement; lines not effective on the date are shown but disabled. */
export const authorisedOptions = (authorised) => {
  const out = [];
  (authorised?.lines || []).forEach((ln) => {
    const dates = ln.service_start_date || ln.service_end_date ? ` ${ln.service_start_date || '…'} → ${ln.service_end_date || '…'}` : '';
    (ln.participant_service_items || []).forEach((p) => {
      out.push({
        value: p.id,
        agreement_line_id: ln.agreement_line_id,
        disabled: ln.effective_on_date === false,
        label: `${ln.service_name || 'Service'}${ln.support_item_number ? ` (${ln.support_item_number})` : ''} · ${p.start_time || '--:--'}–${p.end_time || '--:--'}${dates}${ln.effective_on_date === false ? ' — not effective on this date' : ''}`,
      });
    });
  });
  return out;
};

const MESSAGES = {
  SERVICE_NOT_AUTHORISED: 'That service is not authorised by the participant\'s ACTIVE agreement line — only authorised services can be rostered.',
  AGREEMENT_NOT_ACTIVE: 'Only an ACTIVE service agreement authorises roster services for this participant.',
  PSI_UNAVAILABLE: 'The selected participant service item is not available under the ACTIVE agreement.',
  LINE_NOT_EFFECTIVE: 'The agreement line is not effective on this service date.',
  SHIFT_LOCKED: 'This shift has already been delivered, so its service segments can only be changed by a rostering manager with a reason.',
  SEGMENT_OVERLAP: 'This participant already has a segment covering part of that time.',
  SEGMENT_OUTSIDE_SHIFT: 'The segment must sit inside the participant shift window.',
  INVALID_TIMES: 'Segment end must be after its start (HH:MM).',
  INVALID_SUPPORT_RATIO: 'Choose a ratio of 1:1, 1:2, 1:3 or 2:1.',
  FEATURE_DISABLED: 'Legacy catalogue allocations are disabled; select an authorised service instead.',
  CONCURRENCY_CONFLICT: 'This segment was changed by someone else — reload and try again.',
};

/** Code-first error text: PRD codes get a stable, friendly message; everything else falls back to the server message. */
export const segmentErrorMessage = (err) => {
  const d = err?.response?.data?.detail || err?.response?.data?.error || err?.detail || {};
  const code = typeof d === 'string' ? d : d?.code;
  if (code === 'SHIFT_LOCKED' && d?.details?.reason) {
    return `${lockMessage(d.details.reason)} A rostering manager can add or change a service segment by giving a reason.`;
  }
  if (code && MESSAGES[code]) {
    const extra = d?.details?.reason ? ` (${d.details.reason})` : '';
    return `${MESSAGES[code]}${extra}`;
  }
  return d?.message || code || 'Failed to save segment';
};

// Billing has already used the shift: a segment change does not alter what was billed (the billing record is corrected separately).
export const BILLING_FINAL_LOCKS = new Set(['SHIFT_APPROVED', 'SHIFT_INVOICED', 'SHIFT_BILLED', 'SHIFT_PAID', 'INVOICED', 'BILLING_PROCESSED']);

const LOCK_MESSAGES = {
  CLOCKED: 'A worker has clocked in to this shift.',
  SHIFT_IN_PROGRESS: 'This shift is in progress.',
  SHIFT_COMPLETED: 'This shift has been completed.',
  ATTENDANCE_RECORDED: 'Participant attendance has been recorded for this shift.',
  TIMESHEET_EXISTS: 'A timesheet exists for this shift.',
  SHIFT_APPROVED: 'This shift has been approved for billing.',
  SHIFT_INVOICED: 'This shift has been invoiced.',
  INVOICED: 'This shift has been invoiced.',
  SHIFT_BILLED: 'This shift has been billed.',
  SHIFT_PAID: 'This shift has been billed and paid.',
  BILLING_PROCESSED: 'Billing has been processed for this shift.',
};

export const lockMessage = (reason) => {
  if (!reason) return '';
  return LOCK_MESSAGES[reason] || 'This shift has been delivered.';
};
